import React from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import { object, string } from 'yup';

import mailOpenIcon from 'assets/images/svg/mail-open.svg';
import LocationIcon from 'assets/images/svg/location-icon-new.svg';

import InjectScript from 'components/shared/InjectScript';
import StandardButton from 'components/shared/StandardButton';
import Input from 'components/shared/ui/Input';

import siteStyle from 'utils/siteStyle';
import { usZipCodeRegex } from 'utils/regexp';

const formEnhancer = withFormik({
  mapPropsToValues: () => ({
    email: '',
    zipCode: '',
  }),
  mapPropsToErrors: (props) => (props),
  validateOnMount: true,
  validationSchema: () => (
    object().shape({
      email: string()
        .email('Enter a valid email address')
        .required('Enter a valid email address'),
      zipCode: string().trim()
        .matches(usZipCodeRegex, 'Enter a valid zip code')
        .required('Enter a valid zip code'),
    })
  ),
  handleSubmit: (values, {
    props: { subscribe, trackGAEvent }, setFieldError, resetForm, setSubmitting,
  }) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY, { action: 'nearby_deal_alert' }).then((gRecaptchaResponseData) => {
        subscribe(values.email, values.zipCode, gRecaptchaResponseData, (error) => {
          if (error) {
            if (error === 'City not found') {
              setFieldError('zipCode', error);
            } else if (error === 'This email address already exists.') {
              setFieldError('email', error);
            } else {
              resetForm({ errors: { others: error } });
            }
          } else {
            resetForm();
          }
          setSubmitting(false);
        });
        trackGAEvent({ name: 'golf_special_clicked', label: 'Golf special clicked' }, {
          golf_special_user_email: values.email,
          golf_special_user_zip: values.zipCode,
        });
      });
    });
  },
});

const GolfSpecialsBanner = ({
  className,
  golfSpecials,
  success,
  values,
  touched,
  errors,
  isValid,
  isSubmitting,
  handleChange,
  handleBlur,
  handleSubmit,
  handleReset,
}) => {
  const renderGolfSpecialsForm = (
    <form onSubmit={handleSubmit} onReset={handleReset}>
      <div className="flex flex-col mt-4 gap-2 md:flex-row">
        <InjectScript src={`https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY}`} />
        <Input
          id="golf-specials-email"
          name="email"
          value={values.email}
          touched={touched.email}
          error={errors.email}
          placeholder="Email address"
          containerClassName="md:w-50"
          inputClassName="pl-10 z-30"
          leftIcon={{ src: mailOpenIcon, alt: 'email-open-icon', className: 'w-5 h-5 fill-current text-grey-3' }}
          handleChange={handleChange}
          handleBlur={handleBlur}
          errorClassname="mt-1 text-2.5 leading-none"
        />
        <Input
          id="golf-specials-zipcode"
          name="zipCode"
          value={values.zipCode}
          touched={touched.zipCode}
          error={errors.zipCode}
          placeholder="Zip code"
          containerClassName="md:max-w-[136px]"
          inputClassName="pl-10"
          leftIcon={{ src: LocationIcon, alt: 'Location-icon', className: 'w-6 h-6 fill-current text-grey-3' }}
          handleChange={handleChange}
          handleBlur={handleBlur}
          errorClassname="mt-1 text-2.5 leading-none"
        />
        {errors?.others && (
          <div className="md:hidden font-primary-two -tracking-0.2 text-special-red mb-1 text-2.5 leading-none">
            {errors?.others}
          </div>
        )}
        <StandardButton
          id="golf-specials-submit"
          type="submit"
          text={(
            <p className={siteStyle({
              common: 'text-4 leading-4 -tracking-0.2 whitespace-nowrap',
              // bt: 'font-korolav font-medium',
              default: 'font-primary-two',
            })}
            >
              {golfSpecials?.bannerCtaText}
            </p>
        )}
          disabled={!isValid || isSubmitting}
          heightStyle="h-10"
          widthStyle="md:w-fit md:px-7"
          roundedStyle="rounded"
        />
      </div>
      {errors?.others && (
        <div className="hidden md:block font-primary-two -tracking-0.2 text-special-red mt-1 text-2.5 leading-none">
          {errors?.others}
        </div>
      )}
    </form>
  );

  const renderTitle = (
    <h2 className={siteStyle({
      common: 'text-5 leading-6 -tracking-0.2 text-dark font-bold',
      // bt: 'font-korolav uppercase',
      default: 'font-primary',
    })}
    >
      {success ? 'Thanks for signing up!' : golfSpecials?.bannerTitle}
    </h2>
  );

  const renderDescription = (
    <p className="font-primary-two text-4 leading-5 -tracking-0.2 text-grey-3 mt-2.5">
      {success
        ? "We've confirmed your email, golf specials will be headed your way."
        : golfSpecials?.bannerDescription}
    </p>
  );

  return (
    <section className={className}>
      <div className="w-full max-w-300 border border-light-grey-four rounded-2 px-4 py-6 md:flex md:gap-10 md:px-10">
        <div className="w-10 h-10 md:min-w-28 md:w-28 md:h-28">
          <img
            src={golfSpecials?.bannerImageUrl}
            alt="Golf Specials Banner"
            className="w-full h-full object-center object-cover"
          />
        </div>
        <div className="mt-6 md:mt-0">
          {renderTitle}
          {renderDescription}
          {!success && renderGolfSpecialsForm}
        </div>
      </div>
    </section>
  );
};

GolfSpecialsBanner.defaultProps = {
  className: '',
};

GolfSpecialsBanner.propTypes = {
  className: PropTypes.string,
  golfSpecials: PropTypes.shape({
    bannerCtaText: PropTypes.string,
    bannerDescription: PropTypes.string,
    bannerImageUrl: PropTypes.string,
    bannerTargetUrl: PropTypes.string,
    bannerTitle: PropTypes.string,
    bannerType: PropTypes.string,
  }).isRequired,
  success: PropTypes.bool.isRequired,
  values: PropTypes.shape({
    email: PropTypes.string,
    zipCode: PropTypes.string,
  }).isRequired,
  touched: PropTypes.shape({
    email: PropTypes.bool,
    zipCode: PropTypes.bool,
  }).isRequired,
  errors: PropTypes.shape({
    email: PropTypes.string,
    zipCode: PropTypes.string,
    others: PropTypes.string,
  }).isRequired,
  isValid: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
};

export default formEnhancer(GolfSpecialsBanner);
